import React, { useEffect, useRef } from "react";
import { ArrowRight, ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?auto=format&fit=crop&w=2400&q=80";

export const Hero = () => {
  const bgRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      if (!bgRef.current) return;
      const y = window.scrollY;
      bgRef.current.style.transform = `translate3d(0, ${y * 0.25}px, 0) scale(1.08)`;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden flex items-end pb-20 pt-32 noise"
    >
      {/* Parallax bg */}
      <div className="absolute inset-0 -z-10">
        <img
          ref={bgRef}
          src={HERO_IMG}
          alt=""
          aria-hidden="true"
          className="hero-bg absolute inset-0 w-full h-full object-cover brightness-110 contrast-110"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#181C24]/95 via-[#181C24]/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#181C24]" />
        <div className="absolute inset-0 grid-overlay opacity-20" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Top metadata strip */}
        <div className="fade-up flex flex-wrap items-center gap-6 mb-12 font-mono-tech text-[11px] tracking-[0.25em] uppercase text-white/50">
          <span className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-[#F8BC33] rounded-full animate-pulse" />
            Operación 24/7
          </span>
          <span className="hidden sm:inline">25° 25′ N · 101° 00′ W</span>
          <span className="hidden md:inline">Coahuila · México</span>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-9">
            <h1
              data-testid="hero-headline"
              className="fade-up font-display font-extrabold tracking-tight text-white text-[44px] leading-[0.95] sm:text-6xl lg:text-[88px] xl:text-[104px]"
            >
              Infraestructura{" "}
              <span className="text-[#F8BC33]">tecnológica</span>
              <br />
              para la industria{" "}
              <span className="relative inline-block">
                de Saltillo
                <span className="absolute -bottom-2 left-0 w-full h-[6px] bg-[#F8BC33]" />
              </span>
              .
            </h1>

            <p
              data-testid="hero-subtitle"
              className="fade-up mt-8 max-w-2xl text-base sm:text-lg text-white/70 leading-relaxed"
              style={{ animationDelay: "0.15s" }}
            >
              Diseñamos, instalamos y damos soporte a sistemas críticos de
              videovigilancia, control de acceso, redes empresariales, fibra
              óptica, HVAC y construcción para maquiladoras y parques
              industriales del noreste mexicano. Garantizamos{" "}
              <span className="text-white">continuidad operativa</span> y{" "}
              <span className="text-white">seguridad perimetral</span>.
            </p>

            <div
              className="fade-up mt-10 flex flex-wrap items-center gap-4"
              style={{ animationDelay: "0.3s" }}
            >
              <button
                type="button"
                onClick={() => scrollTo("#contacto")}
                data-testid="hero-cta-contact"
                className="btn-supernova inline-flex items-center gap-3 px-7 py-4 text-sm font-bold uppercase tracking-wider"
              >
                Solicitar Cotización <ArrowRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => scrollTo("#servicios")}
                data-testid="hero-cta-services"
                className="btn-outline inline-flex items-center gap-3 px-7 py-4 text-sm font-bold uppercase tracking-wider"
              >
                Ver servicios <ArrowDown className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="lg:col-span-3 lg:pl-8 lg:border-l lg:border-white/10">
            <div
              className="fade-up grid grid-cols-3 lg:grid-cols-1 gap-6 lg:gap-8"
              style={{ animationDelay: "0.45s" }}
            >
              {[
                { k: "+15", v: "Años en el sector" },
                { k: "200+", v: "Sitios industriales" },
                { k: "24/7", v: "Soporte técnico" },
              ].map((s) => (
                <div key={s.k} data-testid={`hero-stat-${s.k}`}>
                  <div className="font-display font-extrabold text-3xl lg:text-4xl text-white">
                    {s.k}
                  </div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50 mt-2">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
